import React, { useContext, useState } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { Box, Button, constants, Loading, Text } from '@match/react-component-library';
import { URL_CODE } from '@match/app-rules-resolver';
import {
  useBuildContactUsSC8Request,
  useContactUsEmail,
  useContactUsSelections,
  useContactUsTextArea,
  useSc8DropDownChoices,
  useNameInput,
  useWindowSize,
} from 'modules/hooks';
import api from 'utils/api-services';
import { SiteDataContext } from 'App';
import { useSiteData } from 'modules/hooks';
import { DESKTOP_MIN } from 'modules/components/Router/Router';
import CategoryDropdown from './CategoryDropdown';
import DeviceDropdown from './DeviceDropdown';
import EmailAddress from './EmailAddress';
import NameInput from './NameInput';
import QuestionTextArea from './QuestionTextArea';

const { SPACE } = constants;

const emailRegex = /^[^@\s]+@[^\.\s]+\.[^\s]+$/;

const ContactUsSC8: React.FunctionComponent = () => {
  const { isLoggedIn } = useContext(SiteDataContext);
  const [postRequestPending, setPostRequestPending] = useState<boolean>();
  const [contactUsFormCompleted, setContactUsFormCompleted] = useState<boolean>(false);

  const [width] = useWindowSize();

  const { questionText, onQuestionTextChange } = useContactUsTextArea();
  const { emailAddress, emailError, onEmailAdd, onEmailError } = useContactUsEmail();
  const { nameInput, nameInputError, onNameInputAdd, onNameInputError } = useNameInput();

  const {
    categorySelection,
    deviceSelection,
    onCategorySelect,
    onDeviceSelect,
  } = useContactUsSelections();
  const { categoryChoices, deviceChoices } = useSc8DropDownChoices();

  const { requestBody } = useBuildContactUsSC8Request({
    categorySelection,
    deviceSelection,
    emailAddress,
    nameInput,
    questionText,
  });

  const siteData = useSiteData();
  let brandEndpoint = 'matchgrouplatam';

  switch (siteData.urlCode) {
    case URL_CODE.PAR_PERFEITO:
      brandEndpoint = 'parperfeito';
      break;
    case URL_CODE.DIVINO_AMOR:
      brandEndpoint = 'divinoamor';
      break;
    case URL_CODE.OUR_TIME_BR:
      brandEndpoint = 'ourtime';
      break;
    case URL_CODE.MATCH_BR:
      brandEndpoint = 'matchbr';
      break;
    default:
      brandEndpoint = 'matchgrouplatam';
  }

  const handleSubmit = () => {
    if (emailAddress && !emailAddress.trim().match(emailRegex)) {
      onEmailError(true);
    }
    if (!isLoggedIn && (!nameInput || nameInput === '')) {
      onNameInputError(true);
    } else {
      if (nameInputError) {
        onNameInputError(false);
      }
      if (emailError) {
        onEmailError(false);
      }
      setPostRequestPending(true);
      api
        .postQuestionSubmitLatamZendesk(requestBody, brandEndpoint)
        .then(() => {
          setPostRequestPending(false);
          setContactUsFormCompleted(true);
        })
        .catch((error) => new Error(error));
    }
  };

  const pageWidth = width > DESKTOP_MIN ? '50%' : '100%';

  const buttonDisabled =
    questionText.length === 0 ||
    !emailAddress ||
    emailAddress.length === 0 ||
    (!isLoggedIn && (!nameInput || nameInput === '')) ||
    !deviceSelection ||
    !categorySelection;

  if (postRequestPending) {
    // TODO Tentative loading state while POST request goes through
    // I just hated the "lag", but this is subject to change
    return (
      <Box>
        <Loading />
      </Box>
    );
  }

  if (contactUsFormCompleted) {
    return (
      <Box>
        <FormattedMessage {...messages.submissionCompleted} />
      </Box>
    );
  }

  return (
    <Box width={pageWidth} paddingX={SPACE.MEDIUM}>
      <Box marginBottom={constants.SPACE.XXLARGE}>
        <Text typography={constants.TYPOGRAPHY.HEADING5}>
          <FormattedMessage {...messages.sc8ContactUsTitle} />
        </Text>
      </Box>
      {!isLoggedIn && (
        <NameInput
          nameInput={nameInput}
          nameInputError={nameInputError}
          onNameInputAdd={onNameInputAdd}
        />
      )}
      <EmailAddress emailAddress={emailAddress} emailError={emailError} onEmailAdd={onEmailAdd} />
      <CategoryDropdown onCategorySelect={onCategorySelect} categoryChoices={categoryChoices} />
      <DeviceDropdown onDeviceSelect={onDeviceSelect} deviceChoices={deviceChoices} />
      <QuestionTextArea onQuestionAdd={onQuestionTextChange} questionText={questionText} />
      <Button onClick={handleSubmit} disabled={buttonDisabled}>
        <FormattedMessage {...messages.submitButton} />
      </Button>
    </Box>
  );
};

export default ContactUsSC8;

const messages = defineMessages({
  sc8ContactUsTitle: {
    id: 'src/modules/pages/ContactUsPage/ContactUsSC8/ContactUsSC8:sc8ContactUsTitle',
    description: 'sc8 title text',
    defaultMessage:
      'Complete the form below and submit your question. Our team will answer you shortly!',
  },
  submitButton: {
    id: 'src/modules/pages/ContactUsPage/ContactUsSC3/ContactUsSC3:submitButton',
    description: 'submit button text',
    defaultMessage: 'Submit',
  },
  submissionCompleted: {
    id: 'src/modules/pages/ContactUsPage/ContactUsSC3/ContactUsSC3:submissionCompleted',
    description: 'form submission completed message',
    defaultMessage: 'Your form has been submitted. Thanks.', // TODO: Add actual something
  },
});
